'use client';

import { Suspense, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { gsap } from '@/lib/gsap';
import { AccountDeletionForm } from './AccountDeletionForm';

// Shown when Meta's data-deletion callback redirects the user here with a
// confirmation code (?code=...). Must live under <Suspense> because
// useSearchParams bails out of static rendering.
function MetaConfirmationBanner() {
  const searchParams = useSearchParams();
  const code = searchParams.get('code');

  if (!code) return null;

  return (
    <div
      className="mx-auto mb-0 mt-28 w-full max-w-3xl rounded-2xl border border-green-500/30 bg-green-500/10 p-6 text-center"
      role="status"
    >
      <h2 className="mb-2 text-lg font-semibold text-green-400">
        Deletion Request Received
      </h2>
      <p className="text-green-400/80">
        Your data deletion request is being processed. Confirmation code:{' '}
        <strong className="font-mono text-green-300">{code}</strong>
      </p>
      <p className="mt-2 text-sm text-green-400/60">
        Keep this code as proof of your request. Processing completes within 48 hours.
      </p>
    </div>
  );
}

export function AccountDeletionPageClient() {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const formTitleRef = useRef<HTMLHeadingElement>(null);
  const infoTitleRef = useRef<HTMLHeadingElement>(null);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!heroRef.current) return;

    const ctx = gsap.context(() => {
      // Animate hero title letters
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);

        shuffledLetters.forEach((letter, index) => {
          gsap.to(letter, {
            duration: 0.6,
            scaleY: 1,
            opacity: 1,
            ease: 'power3.out',
            delay: 0.3 + index * 0.03,
          });
        });

        setTimeout(() => {
          applyHoverEffect(letters);
        }, 1500);
      }

      // Animate subtitle
      if (subtitleRef.current) {
        gsap.set(subtitleRef.current, { y: 30, opacity: 0 });
        gsap.to(subtitleRef.current, {
          y: 0,
          opacity: 1,
          duration: 0.7,
          delay: 1.2,
          ease: 'power3.out',
        });
      }

      // Animate form section title on scroll
      const formLetters = formTitleRef.current?.querySelectorAll('.form-letter');
      if (formLetters) {
        gsap.set(formLetters, { scaleY: 0.3, opacity: 0 });

        gsap.to(formLetters, {
          scrollTrigger: {
            trigger: formTitleRef.current,
            start: 'top 80%',
          },
          scaleY: 1,
          opacity: 1,
          duration: 0.6,
          stagger: {
            each: 0.03,
            from: 'random',
          },
          ease: 'power3.out',
          onComplete: () => {
            applyHoverEffect(formLetters);
          },
        });
      }

      // Animate info section title on scroll
      const infoLetters = infoTitleRef.current?.querySelectorAll('.info-letter');
      if (infoLetters) {
        gsap.set(infoLetters, { scaleY: 0.3, opacity: 0 });

        gsap.to(infoLetters, {
          scrollTrigger: {
            trigger: infoTitleRef.current,
            start: 'top 80%',
          },
          scaleY: 1,
          opacity: 1,
          duration: 0.6,
          stagger: {
            each: 0.03,
            from: 'random',
          },
          ease: 'power3.out',
          onComplete: () => {
            applyHoverEffect(infoLetters);
          },
        });
      }
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const heroTitle = 'ACCOUNT DELETION';

  return (
    <div ref={heroRef}>
      {/* Hero Section */}
      <section
        className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#173835' }}
      >
        <Suspense fallback={null}>
          <MetaConfirmationBanner />
        </Suspense>

        {/* Small label */}
        <p className="mb-8 text-sm font-medium uppercase tracking-[0.3em] text-white/40">
          All Datacode Apps &amp; Services
        </p>

        {/* Large title with letter animation */}
        <h1
          ref={titleRef}
          className="font-six-caps text-[calc(1rem+12vw)] md:text-[calc(1rem+16vw)] leading-[0.85] text-white lowercase text-center px-4"
        >
          {heroTitle.split('').map((char, i) => (
            <span
              key={i}
              className="letter inline-block"
              style={{ transformOrigin: 'center bottom' }}
            >
              {char === ' ' ? ' ' : char}
            </span>
          ))}
        </h1>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="max-w-3xl px-8 text-center text-lg leading-relaxed text-white/60 md:text-xl"
          style={{ marginTop: '60px' }}
        >
          We respect your privacy. Use this page to request deletion of your account and
          personal data for any Datacode app or service — including data collected through
          Facebook (Meta) Login. Your request will be processed within 48 hours.
        </p>

        {/* Bottom navigation */}
        <div
          className="absolute left-0 right-0"
          style={{
            bottom: 'clamp(2rem, 5vw, 3.5rem)',
            paddingLeft: 'clamp(1.5rem, 8vw, 120px)',
            paddingRight: 'clamp(1.5rem, 8vw, 120px)',
          }}
        >
          <div className="flex items-center justify-between">
            {/* Scroll to Explore - Left */}
            <div className="flex items-center gap-3">
              <span className="text-sm tracking-wider text-white/50">Scroll to Continue</span>
              <svg
                className="h-4 w-4 text-white/50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {/* Submit Request - Right */}
            <a
              href="#deletion-form"
              className="text-sm tracking-wider text-white/50 hover:text-white transition-colors"
            >
              Submit Request
            </a>
          </div>
        </div>
      </section>

      {/* Important Information Section */}
      <section style={{ backgroundColor: '#0c0c0c', paddingTop: '120px', paddingBottom: '120px' }}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 20px' }}>
          {/* Section title */}
          <div style={{ textAlign: 'center', marginBottom: '80px', width: '100%' }}>
            <p className="text-sm font-medium uppercase tracking-[0.3em] text-white/40 mb-6">
              Before You Proceed
            </p>
            <h2
              ref={infoTitleRef}
              className="font-six-caps text-[calc(1rem+8vw)] leading-[0.9] text-white lowercase"
            >
              {'IMPORTANT'.split('').map((char, i) => (
                <span
                  key={i}
                  className={`info-letter inline-block ${char === ' ' ? 'mx-4' : ''}`}
                  style={{ transformOrigin: 'center bottom' }}
                >
                  {char === ' ' ? ' ' : char}
                </span>
              ))}
            </h2>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full" style={{ maxWidth: '1100px' }}>
            {/* Data Removal */}
            <div
              className="rounded-2xl"
              style={{
                backgroundColor: '#1a1a1a',
                padding: '48px 32px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#173835',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px',
                }}
              >
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '12px' }}>
                Data Removal
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                All your personal data, preferences, and activity history will be permanently deleted from our servers.
              </p>
            </div>

            {/* Processing Time */}
            <div
              className="rounded-2xl"
              style={{
                backgroundColor: '#1a1a1a',
                padding: '48px 32px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#173835',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px',
                }}
              >
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '12px' }}>
                48 Hour Processing
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                Your deletion request will be processed within 48 hours. You&apos;ll receive a confirmation email once completed.
              </p>
            </div>

            {/* Irreversible Action */}
            <div
              className="rounded-2xl"
              style={{
                backgroundColor: '#1a1a1a',
                padding: '48px 32px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#173835',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '24px',
                }}
              >
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '12px' }}>
                Irreversible Action
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                This action cannot be undone. Once deleted, your account and data cannot be recovered.
              </p>
            </div>
          </div>

          {/* Facebook / Meta data deletion instructions.
              This block makes the page valid as a Meta "Data Deletion
              Instructions URL" — it must spell out concrete user steps. */}
          <div
            id="facebook-data-deletion"
            className="rounded-2xl w-full"
            style={{
              maxWidth: '1100px',
              backgroundColor: '#1a1a1a',
              padding: '48px 40px',
              marginTop: '32px',
            }}
          >
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '16px' }}>
              Signed in with Facebook (Meta)?
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>
              If you used Facebook Login with any of our apps, you can have the data we
              received from Facebook deleted in either of these ways:
            </p>
            <ol style={{ color: 'rgba(255,255,255,0.6)', paddingLeft: '20px', lineHeight: 2 }}>
              <li>
                <strong style={{ color: '#fff' }}>Through Facebook:</strong> Go to{' '}
                <em>Settings &amp; Privacy → Settings → Apps and Websites</em>, find the app,
                select <em>Remove</em>, and check <em>&quot;Delete posts, videos or events&quot;</em>.
                Facebook then sends us an automatic deletion request and shows you a
                confirmation code — your data is removed within 48 hours.
              </li>
              <li>
                <strong style={{ color: '#fff' }}>Through this page:</strong> Fill out the form
                below using the email address linked to your Facebook account and name the app
                or service. We process it the same way, within 48 hours.
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* Deletion Form Section */}
      <section id="deletion-form" style={{ backgroundColor: '#173835', paddingTop: '150px', paddingBottom: '150px' }}>
        <div className="content-padding">
          <div className="mx-auto grid w-full max-w-[1280px] items-start gap-16 lg:grid-cols-[1fr_1.5fr] lg:gap-20">
            {/* Left side - Title */}
            <div className="lg:sticky lg:top-32">
              <h2
                ref={formTitleRef}
                className="font-six-caps text-[calc(1rem+10vw)] lg:text-[clamp(5rem,8vw,9rem)] leading-[0.85] text-white lowercase"
              >
                {'DELETE'.split('').map((char, i) => (
                  <span
                    key={i}
                    className={`form-letter inline-block ${char === ' ' ? 'mx-2' : ''}`}
                    style={{ transformOrigin: 'center bottom' }}
                  >
                    {char === ' ' ? ' ' : char}
                  </span>
                ))}
              </h2>
              <p className="text-white/60 mt-8 text-lg leading-relaxed max-w-md hidden lg:block">
                Select the app or service, provide your account information, and tell us why
                you&apos;re leaving. We&apos;ll process your request and send a confirmation to your email.
              </p>
            </div>

            {/* Right side - Form */}
            <div className="w-full max-w-[720px] justify-self-center lg:justify-self-start">
              <AccountDeletionForm />
            </div>
          </div>
        </div>
      </section>

      {/* Support CTA Section */}
      <section
        className="relative flex min-h-[60vh] flex-col items-center justify-center"
        style={{ backgroundColor: '#0c0c0c' }}
      >
        <p className="text-center text-sm font-medium uppercase tracking-[0.3em] text-white/50 mb-10">
          Need Help Instead?
        </p>
        <Link href="/support" className="group">
          <h2 className="font-six-caps text-[calc(1rem+12vw)] leading-[0.85] text-white lowercase text-center transition-opacity duration-300 group-hover:opacity-80">
            Contact Support
          </h2>
        </Link>
      </section>
    </div>
  );
}
